import { useState, useEffect, useCallback } from 'react';
import { useScrollAnimation } from '../../../hooks/useScrollAnimation';

const SUPABASE_FUNCTIONS_URL = import.meta.env.VITE_PUBLIC_SUPABASE_URL + '/functions/v1';

const plans = [
  {
    name: 'HELIX Esencial',
    price: '12',
    priceAmount: 1200,
    badge: 'Más popular',
    badgeColor: 'bg-amber-700 text-white',
    features: [
      'Envío gratuito',
      'Acceso completo a la app',
      'Kit de 3 tallas incluido',
      'Garantía 1 año',
      'Soporte por email',
    ],
  },
  {
    name: 'HELIX Premium',
    price: '79,99',
    priceAmount: 7999,
    badge: 'Edición especial',
    badgeColor: 'bg-stone-100 text-stone-600',
    features: [
      'Envío express prioritario',
      'Acceso completo a la app',
      'Kit de 3 tallas incluido',
      'Garantía extendida 2 años',
      'Soporte premium dedicado',
      'Grabado personalizado incluido',
    ],
  },
];

const SuccessBanner = ({ onClose }: { onClose: () => void }) => (
  <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
    <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />
    <div className="relative bg-white rounded-3xl p-12 max-w-md w-full text-center">
      <button
        onClick={onClose}
        className="absolute top-5 right-5 w-9 h-9 flex items-center justify-center rounded-full bg-stone-100 hover:bg-stone-200 text-stone-600 transition-colors cursor-pointer"
      >
        <i className="ri-close-line text-lg" />
      </button>
      <div className="w-20 h-20 flex items-center justify-center rounded-full bg-emerald-100 mx-auto mb-6">
        <i className="ri-check-double-line text-4xl text-emerald-600" />
      </div>
      <h3 className="font-display text-3xl font-semibold text-stone-900 mb-3">
        ¡Pago completado!
      </h3>
      <p className="font-body text-stone-500 text-sm leading-relaxed mb-8">
        Tu <strong className="text-stone-800">HELIX Ring</strong> está en camino.
        Recibirás un email de confirmación con el seguimiento de tu pedido.
      </p>
      <button
        onClick={onClose}
        className="whitespace-nowrap bg-stone-900 hover:bg-stone-800 text-white font-body font-medium text-sm px-8 py-3.5 rounded-full cursor-pointer transition-colors"
      >
        Volver al inicio
      </button>
    </div>
  </div>
);

const PricingSection = () => {
  const [selectedPlan, setSelectedPlan] = useState('HELIX Esencial');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showSuccess, setShowSuccess] = useState(false);
  const { ref: titleRef, isVisible: titleVisible } = useScrollAnimation();
  const { ref: cardsRef, isVisible: cardsVisible } = useScrollAnimation({ threshold: 0.05 });

  const currentPlan = plans.find((p) => p.name === selectedPlan) ?? plans[0];

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('payment') === 'success') {
      setShowSuccess(true);
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, []);

  const handleCloseSuccess = useCallback(() => {
    setShowSuccess(false);
  }, []);

  const handleBuy = async () => {
    setLoading(true);
    setError('');
    try {
      const origin = window.location.origin;
      const base = window.location.pathname;
      const res = await fetch(`${SUPABASE_FUNCTIONS_URL}/create-checkout`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          planName: currentPlan.name,
          priceAmount: currentPlan.priceAmount,
          currency: 'eur',
          successUrl: `${origin}${base}?payment=success`,
          cancelUrl: `${origin}${base}?payment=cancelled`,
        }),
      });
      const data = await res.json();
      if (data.url) {
        window.location.href = data.url;
      } else {
        setError(data.error ?? 'Error al procesar el pago. Inténtalo de nuevo.');
        setLoading(false);
      }
    } catch {
      setError('Error de conexión. Comprueba tu internet e inténtalo de nuevo.');
      setLoading(false);
    }
  };

  return (
    <>
      {showSuccess && <SuccessBanner onClose={handleCloseSuccess} />}

      <section id="precio" className="py-28 w-full" style={{ backgroundColor: '#f9f8f6' }}>
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          {/* Header */}
          <div
            ref={titleRef}
            className={`text-center max-w-2xl mx-auto mb-20 scroll-hidden ${titleVisible ? 'scroll-visible' : ''}`}
          >
            <span className="font-body text-xs font-semibold tracking-widest text-amber-700 uppercase block mb-4">
              Elige tu plan
            </span>
            <h2 className="font-display text-4xl lg:text-5xl font-light text-stone-900 leading-tight mb-5">
              Encuentra el HELIX
              <br />
              <em className="not-italic font-semibold">perfecto para ti.</em>
            </h2>
            <p className="font-body text-stone-500 text-base">
              Dos versiones diseñadas para distintos estilos de vida.
              Garantía de devolución de 30 días, sin preguntas.
            </p>
          </div>

          {/* Plans grid */}
          <div ref={cardsRef} className="max-w-3xl mx-auto flex flex-col gap-5">
            {plans.map((plan, i) => (
              <button
                key={plan.name}
                type="button"
                onClick={() => setSelectedPlan(plan.name)}
                className={`text-left p-7 rounded-2xl border-2 transition-all duration-200 cursor-pointer scroll-hidden ${
                  cardsVisible ? 'scroll-visible' : ''
                } ${
                  selectedPlan === plan.name
                    ? 'border-amber-600 bg-white'
                    : 'border-stone-200 bg-white/60 hover:border-stone-300'
                }`}
                style={{ transitionDelay: cardsVisible ? `${i * 0.1}s` : '0s' }}
              >
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <span className={`font-body text-xs font-semibold px-3 py-1 rounded-full ${plan.badgeColor}`}>
                      {plan.badge}
                    </span>
                    <h3 className="font-display text-xl font-semibold text-stone-900 mt-3">
                      {plan.name}
                    </h3>
                  </div>
                  <div className="text-right flex-shrink-0 ml-4">
                    <div className="font-display text-3xl font-semibold text-stone-900">
                      {plan.price}€
                    </div>
                    <div className="font-body text-xs text-stone-400 mt-0.5">pago único</div>
                  </div>
                </div>

                <ul className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {plan.features.map((f) => (
                    <li key={f} className="flex items-center gap-2">
                      <div className="w-4 h-4 flex items-center justify-center flex-shrink-0">
                        <i className="ri-check-line text-sm text-amber-600" />
                      </div>
                      <span className="font-body text-sm text-stone-600">{f}</span>
                    </li>
                  ))}
                </ul>

                {selectedPlan === plan.name && (
                  <div className="mt-4 pt-4 border-t border-amber-100 flex items-center gap-2 text-amber-700">
                    <i className="ri-checkbox-circle-fill text-base" />
                    <span className="font-body text-xs font-semibold">Plan seleccionado</span>
                  </div>
                )}
              </button>
            ))}

            {/* CTA Button */}
            <div className={`scroll-hidden ${cardsVisible ? 'scroll-visible delay-300' : ''}`}>
              {error && (
                <div className="mb-3 bg-red-50 border border-red-200 rounded-xl px-4 py-3 flex items-center gap-2">
                  <i className="ri-error-warning-line text-red-500 flex-shrink-0" />
                  <span className="font-body text-sm text-red-600">{error}</span>
                </div>
              )}
              <button
                type="button"
                onClick={handleBuy}
                disabled={loading}
                className="whitespace-nowrap w-full bg-stone-900 hover:bg-amber-700 disabled:opacity-60 disabled:cursor-not-allowed text-white font-body font-semibold text-base tracking-wide py-5 rounded-2xl cursor-pointer transition-all duration-300 flex items-center justify-center gap-3 group"
              >
                {loading ? (
                  <>
                    <i className="ri-loader-4-line animate-spin" />
                    Preparando tu pago seguro...
                  </>
                ) : (
                  <>
                    <i className="ri-lock-line text-white/60 group-hover:text-white transition-colors" />
                    Comprar {currentPlan.name} — {currentPlan.price}€
                    <i className="ri-arrow-right-line transition-transform group-hover:translate-x-1" />
                  </>
                )}
              </button>
            </div>

            {/* Trust badges */}
            <div className={`grid grid-cols-3 gap-3 scroll-hidden ${cardsVisible ? 'scroll-visible delay-400' : ''}`}>
              {[
                { icon: 'ri-secure-payment-line', text: 'Pago seguro' },
                { icon: 'ri-refresh-line', text: '30 días devolución' },
                { icon: 'ri-truck-line', text: 'Envío asegurado' },
              ].map((b) => (
                <div
                  key={b.text}
                  className="bg-white rounded-xl p-3 flex flex-col items-center gap-1.5 border border-stone-100"
                >
                  <div className="w-6 h-6 flex items-center justify-center">
                    <i className={`${b.icon} text-amber-600 text-base`} />
                  </div>
                  <span className="font-body text-xs text-stone-500 text-center">{b.text}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default PricingSection;
